import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import ServiceOrderCard from './components/ServiceOrderCard';
import ServiceOrderFilters from './components/ServiceOrderFilters';
import CreateOrderModal from './components/CreateOrderModal';
import TimeLogModal from './components/TimeLogModal';
import ServiceOrderStats from './components/ServiceOrderStats';

const ServiceOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    priority: '',
    technician: '',
    serviceType: '',
    startDate: '',
    endDate: ''
  });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isTimeLogModalOpen, setIsTimeLogModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Mock data
  const mockOrders = [
    {
      id: 1,
      number: "2024-001",
      client: {
        name: "ABC Corporation",
        address: "Rua das Flores, 123 - Centro, São Paulo - SP",
        phone: "(11) 3456-7890"
      },
      serviceType: "Manutenção Preventiva",
      priority: "Alta",
      status: "Em Andamento",
      assignedTechnician: {
        id: "joao-silva",
        name: "João Silva"
      },
      description: "Manutenção preventiva dos equipamentos de rede e verificação de conectividade em todos os pontos da empresa.",
      createdAt: "2024-10-31T08:30:00.000Z",
      scheduledDate: "2024-10-31",
      scheduledTime: "09:00",
      estimatedHours: 4,
      timeTracking: {
        totalHours: 2,
        totalMinutes: 30,
        isActive: true
      },
      notes: [
        {
          author: "João Silva",
          content: "Iniciado diagnóstico dos equipamentos principais",
          createdAt: "2024-10-31T09:15:00.000Z"
        }
      ]
    },
    {
      id: 2,
      number: "2024-002",
      client: {
        name: "XYZ Ltda",
        address: "Av. Paulista, 456 - Bela Vista, São Paulo - SP",
        phone: "(11) 2345-6789"
      },
      serviceType: "Instalação",
      priority: "Média",
      status: "Pendente",
      assignedTechnician: {
        id: "maria-santos",
        name: "Maria Santos"
      },
      description: "Instalação de novo sistema de monitoramento e configuração de câmeras de segurança.",
      createdAt: "2024-10-31T10:15:00.000Z",
      scheduledDate: "2024-11-01",
      scheduledTime: "14:00",
      estimatedHours: 6,
      timeTracking: {
        totalHours: 0,
        totalMinutes: 0,
        isActive: false
      },
      notes: []
    },
    {
      id: 3,
      number: "2024-003",
      client: {
        name: "Tech Solutions",
        address: "Rua da Tecnologia, 789 - Vila Olímpia, São Paulo - SP",
        phone: "(11) 4567-8901"
      },
      serviceType: "Suporte Técnico",
      priority: "Alta",
      status: "Pendente",
      assignedTechnician: null,
      description: "Resolução de problemas críticos no servidor principal e restauração de backup.",
      createdAt: "2024-10-31T11:45:00.000Z",
      scheduledDate: "2024-10-31",
      scheduledTime: "16:00",
      estimatedHours: 3,
      timeTracking: {
        totalHours: 0,
        totalMinutes: 0,
        isActive: false
      },
      notes: []
    },
    {
      id: 4,
      number: "2024-004",
      client: {
        name: "Global Services",
        address: "Alameda Santos, 321 - Jardins, São Paulo - SP",
        phone: "(11) 5678-9012"
      },
      serviceType: "Consultoria",
      priority: "Baixa",
      status: "Concluído",
      assignedTechnician: {
        id: "carlos-oliveira",
        name: "Carlos Oliveira"
      },
      description: "Consultoria para otimização de processos e implementação de melhorias no sistema.",
      createdAt: "2024-10-30T14:20:00.000Z",
      scheduledDate: "2024-10-30",
      scheduledTime: "15:00",
      estimatedHours: 2,
      timeTracking: {
        totalHours: 2,
        totalMinutes: 15,
        isActive: false
      },
      notes: [
        {
          author: "Carlos Oliveira",
          content: "Consultoria finalizada com sucesso. Relatório enviado ao cliente.",
          createdAt: "2024-10-30T17:30:00.000Z"
        }
      ]
    },
    {
      id: 5,
      number: "2024-005",
      client: {
        name: "Inovação Digital",
        address: "Rua dos Desenvolvedores, 555 - Pinheiros, São Paulo - SP",
        phone: "(11) 6789-0123"
      },
      serviceType: "Manutenção Corretiva",
      priority: "Média",
      status: "Em Andamento",
      assignedTechnician: {
        id: "ana-costa",
        name: "Ana Costa"
      },
      description: "Correção de falhas no sistema de backup e atualização de software de segurança.",
      createdAt: "2024-10-31T13:10:00.000Z",
      scheduledDate: "2024-10-31",
      scheduledTime: "13:30",
      estimatedHours: 3,
      timeTracking: {
        totalHours: 1,
        totalMinutes: 45,
        isActive: false
      },
      notes: [
        {
          author: "Ana Costa",
          content: "Identificado problema no módulo de backup. Iniciando correção.",
          createdAt: "2024-10-31T14:00:00.000Z"
        }
      ]
    }
  ];

  // Load orders on component mount
  useEffect(() => {
    const loadOrders = async () => {
      setIsLoading(true);
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        setOrders(mockOrders);
        setFilteredOrders(mockOrders);
      } catch (error) {
        console.error('Error loading orders:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadOrders();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...orders];

    // Search filter
    if (filters?.search) {
      const searchLower = filters?.search?.toLowerCase();
      filtered = filtered?.filter(order =>
        order?.number?.toLowerCase()?.includes(searchLower) ||
        order?.client?.name?.toLowerCase()?.includes(searchLower) ||
        order?.description?.toLowerCase()?.includes(searchLower) ||
        order?.serviceType?.toLowerCase()?.includes(searchLower)
      );
    }

    // Status filter
    if (filters?.status) {
      filtered = filtered?.filter(order => order?.status === filters?.status);
    }

    // Priority filter
    if (filters?.priority) {
      filtered = filtered?.filter(order => order?.priority === filters?.priority);
    }

    // Technician filter
    if (filters?.technician) {
      filtered = filtered?.filter(order => 
        order?.assignedTechnician?.id === filters?.technician
      );
    }

    // Service type filter
    if (filters?.serviceType) {
      filtered = filtered?.filter(order => order?.serviceType === filters?.serviceType);
    }

    // Date range filter
    if (filters?.startDate) {
      filtered = filtered?.filter(order => 
        new Date(order.scheduledDate) >= new Date(filters.startDate)
      );
    }

    if (filters?.endDate) {
      filtered = filtered?.filter(order => 
        new Date(order.scheduledDate) <= new Date(filters.endDate)
      );
    }

    // Sort orders
    filtered?.sort((a, b) => {
      let aValue = a?.[sortBy];
      let bValue = b?.[sortBy];

      if (sortBy === 'createdAt' || sortBy === 'scheduledDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredOrders(filtered);
  }, [orders, filters, sortBy, sortOrder]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: '',
      priority: '',
      technician: '',
      serviceType: '',
      startDate: '',
      endDate: ''
    });
  };

  const handleCreateOrder = (newOrder) => {
    setOrders(prev => [newOrder, ...prev]);
  };

  const handleStatusUpdate = (orderId, newStatus) => {
    setOrders(prev => prev?.map(order => 
      order?.id === orderId 
        ? { ...order, status: newStatus }
        : order
    ));
  };

  const handleTimeLog = (orderId) => {
    const order = orders?.find(o => o?.id === orderId);
    setSelectedOrder(order);
    setIsTimeLogModalOpen(true);
  };

  const handleSaveTimeLog = async (timeLog) => {
    // Update order with new time log
    setOrders(prev => prev?.map(order => 
      order?.id === timeLog?.orderId 
        ? {
            ...order,
            timeTracking: {
              ...order?.timeTracking,
              totalHours: order?.timeTracking?.totalHours + Math.floor(timeLog?.duration / 60),
              totalMinutes: order?.timeTracking?.totalMinutes + (timeLog?.duration % 60)
            }
          }
        : order
    ));
  };

  const handleViewDetails = (orderId) => {
    // Navigate to order details or open detailed modal
    console.log('View details for order:', orderId);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const activeOrdersCount = orders?.filter(order => 
    order?.status === 'Em Andamento' || order?.status === 'Pendente'
  )?.length;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Ordens de Serviço
            </h1>
            <p className="text-muted-foreground">
              Gerencie e acompanhe todas as ordens de serviço em campo
            </p>
          </div>
          <div className="flex items-center space-x-3 mt-4 sm:mt-0">
            {/* Sort Options */}
            <div className="hidden sm:flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">Ordenar por:</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSort('createdAt')}
                className={sortBy === 'createdAt' ? 'bg-muted' : ''}
              >
                Data
                <Icon 
                  name={sortBy === 'createdAt' && sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                  size={16} 
                  className="ml-1" 
                />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSort('priority')}
                className={sortBy === 'priority' ? 'bg-muted' : ''}
              >
                Prioridade
                <Icon 
                  name={sortBy === 'priority' && sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                  size={16} 
                  className="ml-1" 
                />
              </Button>
            </div>

            <Button
              onClick={() => setIsCreateModalOpen(true)}
              iconName="Plus"
              iconPosition="left"
            >
              Nova OS
            </Button>
          </div>
        </div>

        {/* Statistics */}
        <ServiceOrderStats orders={orders} />

        {/* Filters */}
        <ServiceOrderFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
        />

        {/* Orders List */}
        <div className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-muted-foreground">Carregando ordens de serviço...</p>
              </div>
            </div>
          ) : filteredOrders?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="ClipboardList" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                {Object.values(filters)?.some(filter => filter) 
                  ? 'Nenhuma ordem encontrada' :'Nenhuma ordem de serviço'
                }
              </h3>
              <p className="text-muted-foreground mb-4">
                {Object.values(filters)?.some(filter => filter)
                  ? 'Tente ajustar os filtros para encontrar ordens de serviço' :'Comece criando sua primeira ordem de serviço'
                }
              </p>
              {!Object.values(filters)?.some(filter => filter) && (
                <Button
                  onClick={() => setIsCreateModalOpen(true)}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Criar Primeira OS
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Results Header */}
              <div className="flex items-center justify-between py-2">
                <p className="text-sm text-muted-foreground">
                  {filteredOrders?.length} ordem{filteredOrders?.length !== 1 ? 's' : ''} encontrada{filteredOrders?.length !== 1 ? 's' : ''}
                </p>
              </div>

              {/* Orders Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {filteredOrders?.map((order) => (
                  <ServiceOrderCard
                    key={order?.id}
                    order={order}
                    onStatusUpdate={handleStatusUpdate}
                    onTimeLog={handleTimeLog}
                    onViewDetails={handleViewDetails}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      {/* Modals */}
      <CreateOrderModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateOrder={handleCreateOrder}
      />
      <TimeLogModal
        isOpen={isTimeLogModalOpen}
        onClose={() => setIsTimeLogModalOpen(false)}
        order={selectedOrder}
        onSaveTimeLog={handleSaveTimeLog}
      />
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav 
        userRole="technician" 
        activeOrders={activeOrdersCount}
      />
      {/* Floating Action Button - Mobile Only */}
      <Button
        onClick={() => setIsCreateModalOpen(true)}
        size="icon"
        className="fixed bottom-20 right-4 w-14 h-14 rounded-full shadow-lg lg:hidden z-40"
      >
        <Icon name="Plus" size={24} />
      </Button>
    </div>
  );
};

export default ServiceOrders;